import moment from "moment";
import {
    ISnowflakeEventData,
    IAdoptEaseConfig,
    IAdoptEaseUsage,
    ISnowflakeDMSFunctionCalls,
    ISnowflakeWorkflowUsageThroughDWM,
    IAdoptEaseAllWorkflow,
    ISnowflakeTraining,
    INeverUsedApp,
    Application,
    Workflow,
    IAdoptEaseWorkflow
} from '../types';
import {checkAdoptEaseApplicationStatus, checkAdoptEaseDriveStatus} from '../util/api';

export function getAdoptEaseWorkflows(usageThroughUnify: ISnowflakeEventData[], driveUsage: ISnowflakeDMSFunctionCalls[]
    , adoptEaseConfig: IAdoptEaseConfig[], adoptEaseUserUsage: IAdoptEaseUsage, userAssignedWorkflows: IAdoptEaseAllWorkflow[],
    workflowUsageThroughDWM: ISnowflakeWorkflowUsageThroughDWM[]) {
    try {
        if (!driveUsage || driveUsage.length === 0) return [];

        const userUsageMap = new Map(
            adoptEaseUserUsage?.usage?.map((item) => [item.workflow, moment(item.remindedOn)])
        );

        const formatDateToLocal = (utcDate: Date) => {
            return moment.utc(utcDate).local().format("YYYY-MM-DD");
        };

        const dateMap: Record<string, boolean> = {};
        driveUsage.forEach(item => {
            const formattedDate = formatDateToLocal(item.date);
            dateMap[formattedDate] = true;
        });

        const uniqueDates = Object.keys(dateMap).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

        const today = moment().startOf("day").format("YYYY-MM-DD");

        const lastLoginDate = uniqueDates[0];
        const previousLoginDate = uniqueDates.length > 1 ? uniqueDates[1] : null;

        const datesToCheck = [lastLoginDate];
        if (lastLoginDate === today && previousLoginDate) {
            datesToCheck.push(previousLoginDate);
        }

        const unifyUsageCheckDate = datesToCheck.length > 1 ? datesToCheck[1] : datesToCheck[0];

        const eligibleWorkflows = adoptEaseConfig
            .filter((config) => config.isActive)
            .map((config) => {
                const workflow = config.workflow;
                const reminderDuration = parseInt(config.reminderDuration, 10);

                const driveUsageItem: ISnowflakeDMSFunctionCalls[] = driveUsage.filter(
                    (item) =>
                        item.modernWorkflow === workflow && datesToCheck.includes(moment.utc(item.date).local().format("YYYY-MM-DD"))
                );

                const isInUsageThroughUnify = usageThroughUnify.some(
                    (item) =>
                        item.workflow === workflow &&
                        (moment.utc(item.eventTime).local().startOf('day').isAfter(unifyUsageCheckDate)
                            || moment.utc(item.eventTime).local().startOf('day').isSame(unifyUsageCheckDate))
                );

                workflowUsageThroughDWM = workflowUsageThroughDWM.map(item => {
                    try {
                        const parsedWorkflow = JSON.parse(item.workflow);
                        item.workflowName = parsedWorkflow.workflow;
                    } catch (error) {
                        console.error("Error parsing workflow JSON:", error);
                    }
                    return item;
                });

                const isInDWMWorkflows = workflowUsageThroughDWM.some(
                    (item) =>
                        item.workflowName === workflow &&
                        (moment.utc(item.clientEventTime).local().startOf('day').isAfter(unifyUsageCheckDate)
                            || moment.utc(item.clientEventTime).local().startOf('day').isSame(unifyUsageCheckDate))
                );

                const assignedWorkflow = userAssignedWorkflows.filter((workflowItem: IAdoptEaseAllWorkflow) => workflowItem.name === workflow);

                if (driveUsageItem && driveUsageItem.length > 0 && !isInUsageThroughUnify && !isInDWMWorkflows && assignedWorkflow && assignedWorkflow.length > 0) {
                    const remindedOn = userUsageMap.has(workflow)
                        ? moment.utc(userUsageMap.get(workflow)).local().startOf('day')
                        : null;

                    const reminderThreshold = moment().subtract(reminderDuration, "days").startOf('day');

                    if (!remindedOn || remindedOn.isBefore(reminderThreshold)) {
                        return {
                            workflow, function: driveUsageItem[0].function,
                            modernWorkflow: driveUsageItem[0].modernWorkflow, cdkuUrl: assignedWorkflow[0].cdkuUrl
                        };
                    }
                }
                return null;
            })
            .filter(Boolean);

        return eligibleWorkflows;
    }
    catch (error) { return null }

}

export function checkApplicationUsage(applicationUsage: ISnowflakeEventData[], trainingDetails: ISnowflakeTraining[]
    , adoptEaseConfig: IAdoptEaseConfig[]) {
    try {
        const appConfigs = adoptEaseConfig.filter(cfg => cfg.application);

        const neverUsedApps: INeverUsedApp[] = [];

        appConfigs.forEach(cfg => {
            const appName = cfg.application;
            const appId = cfg.appId || "";
            const usageCheckDays = parseInt(cfg.usageCheck ?? '0', 10);

            const appUsage = applicationUsage.filter(u => u.application === appName);

            let usedRecently = false;
            if (appUsage.length > 0) {
                usedRecently = appUsage.some(u => {
                    const eventTime = moment.utc(u.eventTime).local();
                    const threshold = moment().subtract(usageCheckDays, "days").startOf('day');
                    return eventTime.isAfter(threshold);
                });
            }

            if (appUsage.length === 0 || !usedRecently) {
                const courseId = cfg.courseId || "";
                const courseTaken = trainingDetails.some(t => t.courseId === courseId) || courseId === "";
                neverUsedApps.push({ appName, appId, courseTaken });
            }
        });
        return {
            neverUsedApps
        };
    }
    catch (error) { return null }
}

export function verifyAndEnableAdoptEaseApplications(adoptEaseConfig: any, applications: Application[], userId: string, token: any) {
    try {
        const applicationNames = Array.isArray(applications) ? applications.map(a => a.name) : [];
        const exists = Array.isArray(adoptEaseConfig) && adoptEaseConfig.some(c => c?.application && applicationNames.includes(c.application));

        exists && checkAdoptEaseApplicationStatus(token, userId);
    }
    catch (error) { return null }
}

export function updateAdoptEaseApplications(neverUsedAppsString: string, applications: Application[]) {
    try {
        const neverUsedApps: INeverUsedApp[] = JSON.parse(neverUsedAppsString);
        if (neverUsedApps) {
            const openedApps: string[] = JSON.parse(localStorage.getItem('opened-apps') || '[]')
            const neverUsedAppsRealTime = neverUsedApps.filter(app => !openedApps.includes(app.appId))

            const applicationIds = Array.isArray(applications) ? applications.map(a => a.id) : [];
            const filteredNeverUsedApps = neverUsedAppsRealTime.filter(app => applicationIds.includes(app.appId));
            return filteredNeverUsedApps;
        }
        return [];
    }
    catch (error) { 
        console.log("Error updating applications:", error);
        return [] }
}

export function verifyAndEnableAdoptEaseDrive(adoptEaseConfig: any, userWorkflows: Workflow[], userId: string, token: any) {
    try {
        const workflowNames: string[] = Array.isArray(userWorkflows) ? userWorkflows.map(a => a.name) : [];

        const configExists = Array.isArray(adoptEaseConfig) && adoptEaseConfig.some(c => c?.workflow && workflowNames.includes(c.workflow));

        configExists && checkAdoptEaseDriveStatus(token, userId);
    }
    catch (error) { return null }
}

export function updateAdoptEaseWorkflows(adoptEaseWorkflowsString: string, userWorkflows: Workflow[],
     adoptEaseDriveFlag: boolean, adoptEaseJournalEntryFlag: boolean, adoptEaseCustomerFlag: boolean,
    adoptEaseVendorFlag: boolean) {
    try {
        if (adoptEaseWorkflowsString) {
           const adoptEaseWorkflows: IAdoptEaseWorkflow[]= JSON.parse(adoptEaseWorkflowsString);
            const workflowMap = userWorkflows?.reduce<Map<string, Workflow>>((acc, w) => {
                if (w?.name) acc.set(w.name, w);
                return acc;
            }, new Map());

            const updated = adoptEaseWorkflows
                .filter(w => {
                    if (!w?.workflow) return false;
                    if (!workflowMap.has(w.workflow)) return false;

                    if (w.workflow === "Journal Entry") {
                        return adoptEaseJournalEntryFlag === true;
                    }

                    if (w.workflow === "Customer") {
                        return adoptEaseCustomerFlag === true;
                    }

                    if (w.workflow === "Vendor") {
                        return adoptEaseVendorFlag === true;
                    }

                    return adoptEaseDriveFlag === true;
                })
                .map(w => {
                    const matched = workflowMap.get(w.workflow);
                    return {
                        ...w,
                        cdkuUrl: matched?.cdkuUrl ?? (w as any).cdkuUrl
                    };
                });

            return updated;
        }
        return [];
    }
    catch (error) { return [] }
}

export function updateAdoptEaseWorkflowsBeforeDriveLaunch(adoptEaseWorkflows: IAdoptEaseWorkflow[], adoptEaseUserUsage: IAdoptEaseUsage, adoptEaseConfig: IAdoptEaseConfig[]) {
    try {
        if (adoptEaseWorkflows && adoptEaseWorkflows.length > 0) {
            const userUsageMap = new Map(
                adoptEaseUserUsage?.usage?.map((item) => [item.workflow, moment(item.remindedOn)]));
                const activeConfigs = adoptEaseConfig.filter(c => c.workflow && c.isActive);
                const configMap = new Map(activeConfigs.map(c => [c.workflow as string, c]));

                const filtered: IAdoptEaseWorkflow[] = [];

                adoptEaseWorkflows.forEach(w => {
                    const cfg = configMap.get(w.workflow);
                    if (!cfg) return;

                    const reminderDuration = parseInt(cfg.reminderDuration, 10);
                    const remindedOn = userUsageMap.has(w.workflow)
                        ? moment.utc(userUsageMap.get(w.workflow)).local().startOf('day')
                        : null;
                    const reminderThreshold = moment().subtract(reminderDuration, 'days').startOf('day');

                    if (!remindedOn || remindedOn.isBefore(reminderThreshold)) {
                        filtered.push(w);
                    }
                });
            return filtered;
        }
        return [];
    }
    catch (error) { 
        console.log("Error updating workflows:", error);
        return [] }
}


